/**
 * The immutable key every namespace keeps beside its changeable handle (I-0681, ADR-113).
 *
 * Jay, 2026-09-24: *"even if a namespace gets an external id it should always keep the generated one
 * as stable key in case the external one needs to change e.g. due to copyright reasons"*.
 *
 * - **Drawn by the I-0657 generator**, so a key has exactly the shape of a generated handle: ten
 *   characters, a letter first, no look-alikes. It is never derived from anything about the person
 *   (AC-K6).
 * - **Immutable and never reused**, which the database enforces (AC-K1, AC-K3). A rename, a trademark
 *   claim or an administrator's reclaim changes the handle and never the key.
 * - **The handle starts as the key** (AC-K7). A namespace nobody gave a custom handle shows its key.
 */
import {
  cryptoRandomIndex,
  generateNamespaceHandle,
  isGeneratedNamespaceHandleShape,
  NAMESPACE_HANDLE_ALPHABET,
  NAMESPACE_HANDLE_LENGTH,
  type RandomIndexType,
} from './namespaceHandle.js';

export const NAMESPACE_KEY_LENGTH = NAMESPACE_HANDLE_LENGTH;

/** The key's alphabet. The migration's `CHECK` and its SQL draw use the same one; a spec pins them. */
export const NAMESPACE_KEY_ALPHABET = NAMESPACE_HANDLE_ALPHABET;

/** One new key. The same draw as a generated handle, which is what lets a handle start as its key. */
export function generateNamespaceKey(random: RandomIndexType = cryptoRandomIndex): string {
  return generateNamespaceHandle(random);
}

/** Whether a string has the shape of a key. It says nothing about whether the key exists. */
export function isNamespaceKeyShape(value: string): boolean {
  return isGeneratedNamespaceHandleShape(value);
}

/** Whether a namespace was given a handle of its own, or still shows its key. */
export function namespaceHasCustomHandle(namespace: { path: string; key: string }): boolean {
  return namespace.path !== namespace.key;
}

/**
 * Which identifier the **displayed** address shows (D-0076, answered by Jay 2026-09-24: _"use the
 * generated id, if no handle exists. use the handle otherwise. the internal id link should still
 * resolve"_).
 *
 * - `handle`: the handle chain (`acme`, `acme/platform`). A namespace without a custom handle shows
 *   its key, because its handle is its key. What this is set to.
 * - `key`: the key alone, in every displayed address.
 *
 * ⚠️ This is the browser-bar form only. Every link FEATit produces for somebody to keep uses the key
 * form whatever this says: `workAreaPermalinkRef` (AC-K12).
 */
export const NAMESPACE_URL_FORM: 'handle' | 'key' = 'handle';

/** What an address is rendered from: the namespace's full handle path and its key. */
export interface INamespaceAddressSubject {
  /** The `/`-joined handle path from the root: `acme`, `acme/platform`. */
  readonly fullPath?: string | null;
  readonly key?: string | null;
}

/**
 * The namespace part of an address (AC-K10), `/`-joined. `undefined` when neither is known, which
 * is an older server; the caller then names the work area by its slug alone.
 */
export function namespaceUrlSegment(
  subject: INamespaceAddressSubject,
  form: 'handle' | 'key' = NAMESPACE_URL_FORM,
): string | undefined {
  const fullPath = subject.fullPath?.trim() || undefined;
  const key = subject.key?.trim() || undefined;
  return form === 'key' ? (key ?? fullPath) : (fullPath ?? key);
}
